/**
 * Country options for the dashboard filter panel's Company and Supplier
 * country dropdowns. Split out of `dashboardFilterUtils` (500-line limit), which
 * re-exports both names so existing imports keep working.
 */

/** Map of ISO country codes to display names (Company Country shows ISO codes). */
export const countryDisplayNames: Record<string, string> = {
  GB: 'United Kingdom', US: 'United States', DE: 'Germany', FR: 'France',
  CN: 'China', IN: 'India', JP: 'Japan', CA: 'Canada', AU: 'Australia',
  IT: 'Italy', ES: 'Spain', NL: 'Netherlands', SE: 'Sweden', CH: 'Switzerland',
  BR: 'Brazil', MX: 'Mexico', KR: 'South Korea', SG: 'Singapore',
  IE: 'Ireland', PL: 'Poland', BE: 'Belgium', AT: 'Austria', DK: 'Denmark',
  NO: 'Norway', FI: 'Finland', PT: 'Portugal', CZ: 'Czech Republic',
  NZ: 'New Zealand', ZA: 'South Africa', TH: 'Thailand', MY: 'Malaysia',
  TW: 'Taiwan', HK: 'Hong Kong', AE: 'United Arab Emirates', SA: 'Saudi Arabia',
  TR: 'Turkey', RO: 'Romania', HU: 'Hungary', PH: 'Philippines', VN: 'Vietnam',
};

/**
 * Derive the sorted, de-duplicated set of country values from a list of records,
 * ordered by display name. Works for both Company Country (ISO codes translated
 * via countryDisplayNames) and Supplier Country (free-text country names that
 * fall through to themselves).
 *
 * @param items - Records each optionally carrying a `country` value (ISO code
 *   or free-text name); blank/nullish countries are ignored.
 * @returns The unique country values, sorted by their display name.
 */
export function deriveCountryOptions(items: Array<{ country?: string | null }>): string[] {
  // Type guard (instead of an `as string[]` cast) so the compiler keeps the
  // narrowed `string` type and would flag any future change to the input shape.
  const countries = items
    .map((s) => s.country)
    .filter((c): c is string => Boolean(c));
  return Array.from(new Set(countries)).sort((a, b) => {
    const nameA = countryDisplayNames[a] || a;
    const nameB = countryDisplayNames[b] || b;
    return nameA.localeCompare(nameB);
  });
}
